import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput, Modal, ActivityIndicator, Alert, useWindowDimensions } from 'react-native';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { complaintsAPI } from '../services/api';
import { Logger } from '../utils/logger';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { WebPageShell } from '../components/common/WebPageShell';
import { formatLocalDate, formatLocalDateTime } from '../utils/dateUtils';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import {
  ComplaintThread,
  mapComplaintThread,
  complaintStatusLabel,
  complaintStatusColors,
  complaintSignOffHint,
} from '../utils/complaintThread';

interface Complaint {
  id: string;
  description: string;
  /** Raw server status: PENDING | AWAITING_PARENT | AWAITING_SCHOOL | RESOLVED. */
  status: string;
  createdDate: string;
  complaintNumber?: string;
  type?: string;
  resolveNotes?: string;
  resolveDate?: string;
  resolvedByName?: string;
  messageCount?: number;
}

export function ComplaintsScreen({ isNested }: { isNested?: boolean } = {}) {
  const { user, selectedStudent } = useAuth();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null);

  // The open concern's conversation. Loaded on demand rather than with the list:
  // a family may have many concerns and only ever opens one at a time.
  const [thread, setThread] = useState<ComplaintThread | null>(null);
  const [isThreadLoading, setIsThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [isResolving, setIsResolving] = useState(false);

  const loadComplaints = useCallback(async () => {
    if (!selectedStudent) {
      setIsLoading(false);
      return;
    }
    try {
      const res = await complaintsAPI.getComplaints(selectedStudent.id);
      const raw = res.data?.Complaints ?? res.data?.complaints ?? (Array.isArray(res.data) ? res.data : []);
      setComplaints(raw.map((c: any) => ({
        id: c.Id || c.id || c.ticketId || c.TicketId || '',
        description: c.Description || c.description || c.subject || c.Subject || '',
        // Kept as the server sent it (upper case): the label and the colour are
        // both derived from it, and lower-casing lost the AWAITING_* distinction.
        status: (c.complaintStatus || c.ComplaintStatus || c.Status || c.status || 'PENDING').toUpperCase(),
        createdDate: c.createdAt || c.CreatedAt || c.CreatedDate || c.createdDate || new Date().toISOString(),
        complaintNumber: c.complaintNumber || c.ComplaintNumber,
        type: c.type || c.Type || 'Support',
        resolveNotes: c.resolveNotes || c.ResolveNotes,
        resolveDate: c.resolveDate || c.ResolveDate,
        resolvedByName: c.resolvedByName || c.ResolvedByName,
        messageCount: c.messageCount ?? c.MessageCount ?? 0,
      })));
    } catch (error) {
      Logger.error('Complaints', 'Failed to load complaints', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedStudent]);

  useEffect(() => {
    loadComplaints();
  }, [loadComplaints]);

  const handleSubmit = async () => {
    if (!description.trim() || !user || !selectedStudent) return;
    try {
      setIsSubmitting(true);
      await complaintsAPI.submitComplaint({
        raisedByUserId: user.id,
        studentId: selectedStudent.id,
        assignedClassId: selectedStudent.classId || '',
        description: description.trim(),
      });
      setDescription('');
      setShowForm(false);
      Alert.alert('Success', 'Complaint submitted successfully');
      await loadComplaints();
    } catch (error) {
      Alert.alert('Error', 'Failed to submit complaint');
      Logger.error('Complaints', 'Failed to submit complaint', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getBadgeStyle = (status: string) => ({ backgroundColor: complaintStatusColors(status).bg });

  const getBadgeTextStyle = (status: string) => ({ color: complaintStatusColors(status).text });

  const formatStatus = (status: string) => complaintStatusLabel(status);

  const openComplaint = async (complaint: Complaint) => {
    setSelectedComplaint(complaint);
    setThread(null);
    setThreadError(null);
    setReplyText('');
    setResolveNotes('');

    try {
      setIsThreadLoading(true);
      const res = await complaintsAPI.getThread(complaint.id);
      setThread(mapComplaintThread(res.data));
    } catch (error) {
      setThreadError('Could not load this conversation.');
      Logger.error('Complaints', 'Failed to load complaint thread', error);
    } finally {
      setIsThreadLoading(false);
    }
  };

  const closeComplaint = () => {
    setSelectedComplaint(null);
    setThread(null);
    setThreadError(null);
  };

  const handleSendReply = async () => {
    const message = replyText.trim();
    if (!selectedComplaint || !message || isSending) return;

    try {
      setIsSending(true);
      const res = await complaintsAPI.postMessage(selectedComplaint.id, message);
      setThread(mapComplaintThread(res.data));
      setReplyText('');
      await loadComplaints();
    } catch (error) {
      Alert.alert('Error', 'Failed to send your reply');
      Logger.error('Complaints', 'Failed to post complaint message', error);
    } finally {
      setIsSending(false);
    }
  };

  // Confirming does NOT close the concern by itself - it stays open until the
  // school confirms too, which is the whole point of the two-sided flow.
  const handleResolve = async () => {
    if (!selectedComplaint || isResolving) return;

    try {
      setIsResolving(true);
      const res = await complaintsAPI.resolveMySide(selectedComplaint.id, resolveNotes);
      setThread(mapComplaintThread(res.data));
      setResolveNotes('');
      await loadComplaints();
    } catch (error) {
      Alert.alert('Error', 'Failed to mark this concern as resolved');
      Logger.error('Complaints', 'Failed to resolve complaint', error);
    } finally {
      setIsResolving(false);
    }
  };

  const handleWithdraw = async () => {
    if (!selectedComplaint || isResolving) return;

    try {
      setIsResolving(true);
      const res = await complaintsAPI.withdrawMyResolution(selectedComplaint.id);
      setThread(mapComplaintThread(res.data));
      await loadComplaints();
    } catch (error) {
      Alert.alert('Error', 'Failed to withdraw your confirmation');
      Logger.error('Complaints', 'Failed to withdraw complaint resolution', error);
    } finally {
      setIsResolving(false);
    }
  };

  const content = (
    <View style={{ flexGrow: 1 }}>
      <TouchableOpacity style={styles.addBtn} onPress={() => setShowForm(true)}>
        <FeatherIcon name="plus" size={18} color="#F97316" />
        <Text style={styles.addBtnText}>Raise a concern or suggestion</Text>
      </TouchableOpacity>

      {isLoading ? (
        <LoadingSpinner message="Loading complaints..." />
      ) : complaints.length === 0 ? (
        <View style={styles.empty}>
          <FeatherIcon name="inbox" size={40} color={colors.textMuted} />
          <Text style={styles.emptyText}>No complaints filed</Text>
        </View>
      ) : (
        <ScrollView showsVerticalScrollIndicator={false} scrollEnabled={!isNested}>
          {complaints.map(complaint => (
            <TouchableOpacity
              key={complaint.id}
              style={styles.card}
              activeOpacity={0.8}
              onPress={() => openComplaint(complaint)}
            >
              <View style={styles.cardTopRow}>
                <View style={[styles.badge, getBadgeStyle(complaint.status)]}>
                  <Text style={[styles.badgeText, getBadgeTextStyle(complaint.status)]}>
                    {formatStatus(complaint.status)}
                  </Text>
                </View>
              </View>
              <Text style={styles.cardDesc}>{complaint.description}</Text>
              <View style={styles.cardFooterRow}>
                <Text style={styles.cardDate}>
                  Raised {formatLocalDate(complaint.createdDate, '—')}
                </Text>
                {!!complaint.messageCount && (
                  <View style={styles.replyCount}>
                    <FeatherIcon name="message-square" size={13} color="#64748B" />
                    <Text style={styles.replyCountText}>{complaint.messageCount}</Text>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}

      <Modal visible={showForm} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={[styles.modal, isMobile && styles.modalMobile]}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>New Complaint</Text>
              <TouchableOpacity onPress={() => setShowForm(false)}>
                <FeatherIcon name="x" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <TextInput
              style={styles.input}
              placeholder="Describe your concern..."
              multiline
              numberOfLines={6}
              value={description}
              onChangeText={setDescription}
              editable={!isSubmitting}
            />
            <View style={styles.modalFooter}>
              <TouchableOpacity
                style={styles.cancelBtn}
                onPress={() => setShowForm(false)}
                disabled={isSubmitting}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.submitBtn, isSubmitting && styles.submitBtnDisabled]}
                onPress={handleSubmit}
                disabled={isSubmitting || !description.trim()}>
                {isSubmitting ? (
                  <ActivityIndicator size="small" color="#FFF" />
                ) : (
                  <Text style={styles.submitBtnText}>Submit</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={!!selectedComplaint} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          {selectedComplaint && (
            <View style={[styles.modal, isMobile && styles.modalMobile]}>
              <View style={styles.modalHeader}>
                <View>
                  <Text style={styles.modalTitle}>Concern</Text>
                  {!!selectedComplaint.complaintNumber && (
                    <Text style={styles.ticketIdText}>{selectedComplaint.complaintNumber}</Text>
                  )}
                </View>
                <TouchableOpacity onPress={closeComplaint}>
                  <FeatherIcon name="x" size={24} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>

              {isThreadLoading && !thread ? (
                <LoadingSpinner message="Loading the conversation..." />
              ) : threadError || !thread ? (
                <Text style={styles.errorText}>{threadError || 'This conversation is unavailable.'}</Text>
              ) : (
                <>
                  <ScrollView style={{ maxHeight: 420 }} showsVerticalScrollIndicator={false}>
                    {/* Who still has to confirm, said plainly. "AWAITING_PARENT"
                        means opposite things to the two sides reading it. */}
                    <View style={styles.signOffBox}>
                      <View style={styles.signOffRow}>
                        <View style={styles.signOffItem}>
                          <FeatherIcon
                            name={thread.isSchoolResolved ? 'check-circle' : 'clock'}
                            size={15}
                            color={thread.isSchoolResolved ? '#059669' : '#B45309'}
                          />
                          <Text style={styles.signOffText}>
                            School: {thread.isSchoolResolved ? 'confirmed' : 'not yet'}
                          </Text>
                        </View>
                        <View style={styles.signOffItem}>
                          <FeatherIcon
                            name={thread.isParentResolved ? 'check-circle' : 'clock'}
                            size={15}
                            color={thread.isParentResolved ? '#059669' : '#B45309'}
                          />
                          <Text style={styles.signOffText}>
                            You: {thread.isParentResolved ? 'confirmed' : 'not yet'}
                          </Text>
                        </View>
                      </View>
                      <Text style={styles.signOffHint}>{complaintSignOffHint(thread)}</Text>
                    </View>

                    {/* The opening text is part of the conversation: put anywhere
                        else, the first reply looks like it answers nothing. */}
                    <View style={[styles.bubble, styles.bubbleOpening]}>
                      <View style={styles.bubbleMeta}>
                        <Text style={[styles.bubbleAuthor, styles.lightBubbleText]}>
                          {thread.type === 'PARENT' ? 'You' : thread.raisedByName || 'The school'}
                        </Text>
                        <Text style={styles.bubbleTime}>
                          {formatLocalDateTime(thread.createdDate, '—')}
                        </Text>
                      </View>
                      <Text style={[styles.bubbleText, styles.lightBubbleText]}>{thread.description}</Text>
                    </View>

                    {thread.messages.map(m => (
                      <View
                        key={m.id}
                        style={[
                          styles.bubble,
                          m.isMine ? styles.bubbleMine : styles.bubbleTheirs,
                          isMobile && (m.isMine ? styles.bubbleMineMobile : styles.bubbleTheirsMobile),
                        ]}
                      >
                        <View style={styles.bubbleMeta}>
                          <Text style={[styles.bubbleAuthor, m.isMine && styles.lightBubbleText]}>
                            {m.isMine ? 'You' : m.senderName || (m.senderRole === 'PARENT' ? 'Parent' : 'School')}
                          </Text>
                          <Text style={styles.bubbleTime}>
                            {formatLocalDateTime(m.createdDate, '—')}
                          </Text>
                        </View>
                        <Text style={[styles.bubbleText, m.isMine && styles.lightBubbleText]}>{m.message}</Text>
                      </View>
                    ))}

                    {thread.messages.length === 0 && (
                      <Text style={styles.emptyThreadText}>No replies yet.</Text>
                    )}
                  </ScrollView>

                  {thread.isClosed ? (
                    <Text style={styles.closedNote}>
                      This concern is closed, so it no longer takes replies. If something
                      else comes up, raise a new one.
                    </Text>
                  ) : (
                    <>
                      <TextInput
                        style={[styles.input, { marginTop: 12, marginBottom: 12, minHeight: 80 }]}
                        placeholder="Write a reply..."
                        multiline
                        numberOfLines={3}
                        value={replyText}
                        onChangeText={setReplyText}
                        editable={!isSending}
                      />
                      <TouchableOpacity
                        style={[styles.sendReplyBtn, (isSending || !replyText.trim()) && styles.submitBtnDisabled]}
                        onPress={handleSendReply}
                        disabled={isSending || !replyText.trim()}
                      >
                        {isSending ? (
                          <ActivityIndicator size="small" color="#FFF" />
                        ) : (
                          <Text style={styles.submitBtnText}>Send reply</Text>
                        )}
                      </TouchableOpacity>

                      {thread.canResolve && (
                        <>
                          <TextInput
                            style={[styles.input, { marginTop: 12, marginBottom: 12, minHeight: 60 }]}
                            placeholder="Closing note (optional)"
                            multiline
                            numberOfLines={2}
                            value={resolveNotes}
                            onChangeText={setResolveNotes}
                            editable={!isResolving}
                          />
                          <TouchableOpacity
                            style={[styles.acknowledgeBtn, isResolving && styles.submitBtnDisabled]}
                            onPress={handleResolve}
                            disabled={isResolving}
                          >
                            {isResolving ? (
                              <ActivityIndicator size="small" color="#FFF" />
                            ) : (
                              <Text style={styles.acknowledgeBtnText}>Mark as resolved</Text>
                            )}
                          </TouchableOpacity>
                        </>
                      )}

                      {/* Confirming is not a trapdoor: while the school has still
                          to agree, a reply can change things. */}
                      {thread.hasMyResolution && (
                        <TouchableOpacity
                          style={styles.withdrawBtn}
                          onPress={handleWithdraw}
                          disabled={isResolving}
                        >
                          <Text style={styles.withdrawBtnText}>Withdraw my confirmation</Text>
                        </TouchableOpacity>
                      )}
                    </>
                  )}
                </>
              )}
            </View>
          )}
        </View>
      </Modal>
    </View>
  );

  if (isNested) return content;
  return <WebPageShell title="Complaints">{content}</WebPageShell>;
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  addBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 14, borderWidth: 1, borderColor: '#F97316', borderStyle: 'dashed', borderRadius: 12, marginBottom: 20, backgroundColor: colors.card, gap: 8 },
  addBtnText: { color: '#F97316', fontWeight: '700', fontSize: 14 },
  loader: { marginTop: 40 },
  empty: { alignItems: 'center', justifyContent: 'center', paddingVertical: 60 },
  emptyText: { fontSize: 16, color: colors.textMuted, marginTop: 12, fontWeight: '600' },
  card: { backgroundColor: colors.card, borderRadius: 12, padding: 16, marginBottom: 12, elevation: 1, shadowColor: colors.shadow, shadowOpacity: 0.04, shadowRadius: 6, shadowOffset: { width: 0, height: 2 } },
  cardTopRow: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 6 },
  ticketId: { fontSize: 13, color: colors.textMuted, fontWeight: '700' },
  cardDate: { fontSize: 13, color: colors.textMuted, fontWeight: '500' },
  badge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 16 },
  badgeText: { fontSize: 12, fontWeight: '700' },
  cardDesc: { fontSize: 15, color: colors.textPrimary, fontWeight: '700', marginBottom: 8 },
  modalOverlay: { flex: 1, backgroundColor: colors.overlay, justifyContent: 'center', alignItems: 'center' },
  modal: { backgroundColor: colors.card, borderRadius: 16, padding: 20, maxHeight: '90%', width: '100%', maxWidth: 500 },
  modalMobile: { padding: 14, borderRadius: 12, maxHeight: '95%' },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  modalTitle: { fontSize: 18, fontWeight: '800', color: colors.textPrimary },
  input: { borderWidth: 1, borderColor: colors.border, borderRadius: 8, padding: 12, marginBottom: 16, color: colors.textPrimary, textAlignVertical: 'top', minHeight: 120 },
  modalFooter: { flexDirection: 'row', gap: 12 },
  cancelBtn: { flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: colors.border, alignItems: 'center' },
  cancelBtnText: { fontSize: 14, fontWeight: '700', color: colors.textSecondary },
  submitBtn: { flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: colors.primary, alignItems: 'center' },
  sendReplyBtn: { backgroundColor: colors.primary, paddingVertical: 12, borderRadius: 8, alignItems: 'center' },
  submitBtnDisabled: { opacity: 0.6 },
  submitBtnText: { fontSize: 14, fontWeight: '700', color: '#FFF' },
  ticketIdText: { fontSize: 13, color: colors.textSecondary, fontWeight: '600', marginTop: 4 },
  detailRowWeb: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: colors.divider },
  detailLabelWeb: { fontSize: 14, color: colors.textSecondary, fontWeight: '600' },
  detailValueWeb: { fontSize: 14, color: colors.textPrimary, fontWeight: '600' },
  detailSectionWeb: { paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: colors.divider },
  detailDescWeb: { fontSize: 15, color: colors.textPrimary, lineHeight: 22, marginTop: 8 },
  resolvedSectionWeb: { backgroundColor: colors.background, borderRadius: 12, padding: 16, marginTop: 16, borderWidth: 1, borderColor: colors.border },
  resolvedTitleWeb: { fontSize: 16, fontWeight: '700', color: colors.textPrimary, marginBottom: 12 },
  acknowledgeBtn: { marginTop: 4, backgroundColor: '#059669', paddingVertical: 12, borderRadius: 8, alignItems: 'center' },
  acknowledgeBtnText: { color: '#FFF', fontSize: 14, fontWeight: '700' },
  withdrawBtn: { marginTop: 12, paddingVertical: 10, alignItems: 'center' },
  withdrawBtnText: { color: colors.textSecondary, fontSize: 13, fontWeight: '600', textDecorationLine: 'underline' },
  errorText: { fontSize: 14, color: '#DC2626', paddingVertical: 16 },
  cardFooterRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  replyCount: { flexDirection: 'row', alignItems: 'center', gap: 5 } as any,
  replyCountText: { fontSize: 13, fontWeight: '600', color: '#64748B' },
  signOffBox: { backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border, borderRadius: 12, padding: 14, marginBottom: 16 },
  signOffRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 18, marginBottom: 8 } as any,
  signOffItem: { flexDirection: 'row', alignItems: 'center', gap: 7 } as any,
  signOffText: { fontSize: 13, fontWeight: '600', color: colors.textPrimary },
  signOffHint: { fontSize: 13, color: colors.textSecondary, lineHeight: 19 },
  bubble: { borderRadius: 14, padding: 12, marginBottom: 10, borderWidth: 1 },
  bubbleOpening: { backgroundColor: '#FFFBEB', borderColor: '#FDE68A' },
  bubbleMine: { backgroundColor: '#EFF6FF', borderColor: '#BFDBFE', marginLeft: 24 },
  bubbleMineMobile: { marginLeft: 8 },
  bubbleTheirs: { backgroundColor: colors.background, borderColor: colors.border, marginRight: 24 },
  bubbleTheirsMobile: { marginRight: 8 },
  bubbleMeta: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 },
  bubbleAuthor: { fontSize: 12, fontWeight: '700', color: colors.textPrimary },
  bubbleTime: { fontSize: 11, color: colors.textMuted },
  bubbleText: { fontSize: 14, color: colors.textPrimary, lineHeight: 20 },
  lightBubbleText: { color: '#000000' },
  emptyThreadText: { fontSize: 13, color: colors.textMuted, paddingVertical: 8 },
  closedNote: { fontSize: 13, color: '#059669', backgroundColor: '#F0FDF4', borderWidth: 1, borderColor: '#BBF7D0', borderRadius: 10, padding: 12, marginTop: 12, lineHeight: 19 },
});
